import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Loader from '../../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../../services/ApiService';
import './RolePermissions.css';

// Modules sensitive enough to call out with a "Restricted" flag in the
// permission list — granting these needs extra care, since they touch
// business-wide stats, customer accounts, membership billing, staff
// attendance/system-user management, the rooms/services that drive pricing,
// or admin-level config (roles, system settings, site pages).
const RESTRICTED_PARENTS = new Set([
    'Dashboard',
    'Account',
    'Membership Plans',
    'User Management',
    'Attendance',
    'Rooms',
    'Services',
    'System Users',
    'Roles & Permissions',
    'System Configuration',
    'Pages',
]);

const RoleForm = () => {
    const [isLoader, setIsLoader] = useState(false);
    const [permissions, setPermissions] = useState([]);
    const [name, setName] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedGroups, setExpandedGroups] = useState(new Set([0])); // Only first group expanded by default
    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        getPermissions();
    }, []);

    const getPermissions = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "GET",
                url: `admin/role-permissions/${id || ''}`,
            });
            const data = response.data;
            if (data.status) {
                setName(data.data.role?.name || '');
                const perms = data.data.permissions;
                
                // Process permissions: calculate parent checkbox state based on children
                const processedPerms = perms.map(tab => {
                    const allSelected = tab.items.length > 0 && tab.items.every((item) => item.isChecked === true);
                    return {
                        ...tab,
                        isParentChecked: allSelected
                    };
                });
                
                setPermissions(processedPerms);
            } else {
                toast.error(data.message);
            }
            setIsLoader(false);
        } catch (error) {
            setIsLoader(false);
            console.log(error);
        }
    };

    // Filter permissions based on search query
    const filteredPermissions = useMemo(() => {
        if (!searchQuery.trim()) {
            return permissions.map((tab, idx) => ({ 
                ...tab, 
                originalIndex: idx
            }));
        }
        
        const query = searchQuery.toLowerCase();
        return permissions.map((tab, idx) => {
            const filteredItems = tab.items.filter(item => 
                item.name.toLowerCase().includes(query) ||
                tab.parent.toLowerCase().includes(query)
            );
            
            if (filteredItems.length === 0 && !tab.parent.toLowerCase().includes(query)) {
                return null;
            }
            
            return {
                ...tab,
                items: filteredItems.length > 0 ? filteredItems : tab.items,
                originalIndex: idx
            };
        }).filter(Boolean);
    }, [permissions, searchQuery]);

    // Calculate total selected permissions
    const totalSelected = useMemo(() => {
        return permissions.reduce((total, tab) => {
            return total + tab.items.filter(item => item.isChecked === true).length;
        }, 0);
    }, [permissions]);

    // Toggle parent checkbox - select/deselect all children
    const handleParentToggle = (groupIndex) => {
        setPermissions(prevPermissions => {
            return prevPermissions.map((group, idx) => {
                if (idx === groupIndex) {
                    // Check current state: are all children selected?
                    const allSelected = group.items.length > 0 && group.items.every(item => item.isChecked === true);
                    const newState = !allSelected; // Toggle to opposite state
                    
                    return {
                        ...group,
                        isParentChecked: newState,
                        items: group.items.map(item => ({
                            ...item,
                            isChecked: newState
                        }))
                    };
                }
                return group;
            });
        });
    };

    // Toggle individual permission checkbox
    const handleChildToggle = (groupIndex, itemIndex) => {
        setPermissions(prevPermissions => {
            return prevPermissions.map((group, idx) => {
                if (idx === groupIndex) {
                    const updatedItems = group.items.map((item, iIdx) => {
                        if (iIdx === itemIndex) {
                            return {
                                ...item,
                                isChecked: !item.isChecked
                            };
                        }
                        return item;
                    });
                    
                    // Update parent checkbox state based on children
                    const allSelected = updatedItems.length > 0 && updatedItems.every(item => item.isChecked === true);
                    
                    return {
                        ...group,
                        items: updatedItems,
                        isParentChecked: allSelected
                    };
                }
                return group;
            });
        });
    };

    // Select all permissions
    const selectAllPermissions = () => {
        setPermissions(prevPermissions => {
            return prevPermissions.map(group => ({
                ...group,
                isParentChecked: true,
                items: group.items.map(item => ({ 
                    ...item, 
                    isChecked: true 
                }))
            }));
        });
    };

    // Deselect all permissions
    const deselectAllPermissions = () => {
        setPermissions(prevPermissions => {
            return prevPermissions.map(group => ({
                ...group,
                isParentChecked: false,
                items: group.items.map(item => ({ 
                    ...item, 
                    isChecked: false 
                }))
            }));
        });
    };

    // Toggle accordion group expand/collapse
    const toggleGroup = (groupIndex) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupIndex)) {
                newSet.delete(groupIndex);
            } else {
                newSet.add(groupIndex);
            }
            return newSet;
        });
    };

    const submitForm = async (e) => {
        e.preventDefault();
        setIsLoader(true);
        try {
            const response = await ApiService.request({
                method: "POST",
                url: `admin/roles/createOrUpdate/${id || ''}`,
                data: { name, permissions }
            });
            const data = response.data;
            if (data.status) {
                toast.success(data.message);
                navigate('/admin/roles');
            } else {
                toast.error(data.message);
            }
            setIsLoader(false);
        } catch (error) {
            console.error('Submission failed:', error);
            setIsLoader(false);
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            {/* Toolbar */}
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            {id ? 'Edit' : 'Add New'} Role
                        </h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Dashboard</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/roles" className="text-muted text-hover-primary">Roles</Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    <div className="card">
                        <div className="card-body pt-0">
                            <form id="kt_modal_new_target_form" className="form" onSubmit={submitForm}>
                                <div className="row g-9 my-3">
                                    <div className="col-md-12 fv-row">
                                        <label className="required fs-6 fw-semibold mb-2">Name</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-solid"
                                            placeholder="Enter Role Name"
                                            required
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <hr />
                                
                                {/* Permissions Section Header */}
                                <div className="permissions-header mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div>
                                            <h4 className="mb-1">Permissions</h4>
                                            <p className="text-muted mb-0">
                                                Select permissions for this role
                                                {totalSelected > 0 && (
                                                    <span className="badge bg-primary ms-2">
                                                        {totalSelected} selected
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={selectAllPermissions}
                                            >
                                                Select All
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={deselectAllPermissions}
                                            >
                                                Deselect All
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* Search Bar */}
                                    <div className="permission-search-wrapper">
                                        <input
                                            type="text"
                                            className="form-control form-control-solid"
                                            placeholder="Search permissions..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        <i className="bi bi-search permission-search-icon"></i>
                                    </div>
                                </div>

                                {permissions.length > 0 ? (
                                    <div className="permissions-container">
                                        {filteredPermissions.length > 0 ? (
                                            <div className="permission-groups-wrapper">
                                                {filteredPermissions.map((group, filteredIdx) => {
                                                    const groupIndex = group.originalIndex !== undefined 
                                                        ? group.originalIndex 
                                                        : permissions.findIndex(p => p.parent === group.parent);
                                                    
                                                    const actualGroup = permissions[groupIndex];
                                                    if (!actualGroup) return null;
                                                    
                                                    const selectedCount = actualGroup.items.filter(item => item.isChecked === true).length;
                                                    const totalCount = actualGroup.items.length;
                                                    const isExpanded = expandedGroups.has(groupIndex);
                                                    
                                                    // Parent checkbox is checked if all children are selected
                                                    const isParentChecked = actualGroup.isParentChecked === true;
                                                    
                                                    return (
                                                        <div key={`group-${groupIndex}`} className="permission-group-card">
                                                            {/* Group Header */}
                                                            <div 
                                                                className="permission-group-header"
                                                                onClick={() => toggleGroup(groupIndex)}
                                                            >
                                                                <div className="d-flex align-items-center w-100">
                                                                    {/* Custom Parent Checkbox */}
                                                                    <div 
                                                                        className="custom-checkbox-container"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleParentToggle(groupIndex);
                                                                        }}
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            id={`parent-checkbox-${groupIndex}`}
                                                                            className="custom-checkbox-input"
                                                                            checked={isParentChecked}
                                                                            onChange={() => handleParentToggle(groupIndex)}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        />
                                                                        <span className="custom-checkbox-checkmark"></span>
                                                                    </div>
                                                                    
                                                                    <div className="flex-grow-1 ms-3">
                                                                        <span className="permission-group-title">
                                                                            {group.parent || `Permission Group ${groupIndex + 1}`}
                                                                        </span>
                                                                        {RESTRICTED_PARENTS.has(group.parent) && (
                                                                            <span className="permission-restricted-badge">Restricted</span>
                                                                        )}
                                                                        <span className="permission-group-count">
                                                                            ({selectedCount}/{totalCount} selected)
                                                                        </span>
                                                                    </div>
                                                                    
                                                                    <div className="permission-group-arrow">
                                                                        <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Group Content */}
                                                            {isExpanded && (
                                                                <div className="permission-group-body">
                                                                    <div className="permission-items-grid">
                                                                        {group.items.map((item, itemIdx) => {
                                                                            // Find the actual item in the original group
                                                                            const actualItemIndex = actualGroup.items.findIndex(i => i.name === item.name);
                                                                            if (actualItemIndex === -1) return null;
                                                                            
                                                                            const actualItem = actualGroup.items[actualItemIndex];
                                                                            const isItemChecked = actualItem.isChecked === true;
                                                                            
                                                                            return (
                                                                                <div 
                                                                                    key={`item-${groupIndex}-${actualItemIndex}`} 
                                                                                    className="permission-item-card"
                                                                                >
                                                                                    <div 
                                                                                        className="custom-checkbox-container"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleChildToggle(groupIndex, actualItemIndex);
                                                                                        }}
                                                                                    >
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            id={`child-checkbox-${groupIndex}-${actualItemIndex}`}
                                                                                            className="custom-checkbox-input"
                                                                                            checked={isItemChecked}
                                                                                            onChange={() => handleChildToggle(groupIndex, actualItemIndex)}
                                                                                            onClick={(e) => e.stopPropagation()}
                                                                                        />
                                                                                        <span className="custom-checkbox-checkmark"></span>
                                                                                    </div>
                                                                                    <label 
                                                                                        htmlFor={`child-checkbox-${groupIndex}-${actualItemIndex}`}
                                                                                        className="permission-item-label"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleChildToggle(groupIndex, actualItemIndex);
                                                                                        }}
                                                                                    >
                                                                                        {item.name}
                                                                                    </label>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <i className="bi bi-search fs-1 text-muted d-block mb-3"></i>
                                                <p className="text-muted">No permissions found matching "{searchQuery}"</p>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <i className="bi bi-shield-x fs-1 text-muted d-block mb-3"></i>
                                        <p className="text-muted">No permissions available</p>
                                    </div>
                                )}

                                <div className="row g-9 my-3">
                                    <div className="text-center">
                                        <Link to="/admin/roles" id="kt_modal_new_target_cancel" className="btn btn-light me-3">
                                            Back
                                        </Link>
                                        <button type="submit" className="btn btn-base" disabled={isLoader}>
                                            {isLoader ? <Loader size={20} text="Submitting" /> : 'Submit'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RoleForm;
